import { useQuery } from "@tanstack/react-query";

import {
  api,
  type AgentReplies,
  type ReplyBreakdown,
  type ReplyDistribution,
  type ReportScope,
} from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { Select } from "../../ui/Select";
import { rangeIsReady, toRangeValue } from "../RangeFilter";
import { ReportCard } from "../card/ReportCard";
import type { ChartType } from "../card/chart-types";

/**
 * How many replies it takes this agent to close a ticket.
 *
 * An efficiency reading and only that. It counts what the desk sent and never
 * what the customer did, so a talkative account cannot make their agent look
 * slow here — which is what makes it fair to compare two people with it.
 *
 * **The median is the figure; p90 and p95 are the warning.** Reply counts are
 * long-tailed — most tickets take one or two and the occasional returns
 * argument takes fifteen — so a mean describes neither and lurches when a
 * single bad ticket lands. The card shows the typical ticket large and the tail
 * small beside it, because both are needed: a median alone hides somebody whose
 * difficult tickets run away, and a tail alone punishes whoever gets handed the
 * difficult ones.
 *
 * **Agent views only.** On the desk-wide view this would be a league table of
 * named colleagues ranked by a number none of them agreed to be ranked on, and
 * Pulse deliberately has no such screen — the same reason `compare=team`
 * returns one aggregate and never a breakdown by person.
 *
 * The breakdown sits beside the date range in the card's own filter cluster,
 * because it is the same kind of control: both narrow what the number is about.
 * It offers pipeline and channel and not priority — a Pulse ticket has no
 * priority field, and a filter over a column that does not exist would have to
 * invent the column first.
 */

/* Table only. Every other chart here would draw a median as though it were a
   quantity you could stack or slice, and a donut of "2, 3, 8" divides a whole
   that has no parts. The card is a number and a small table, and says so. */
const CHARTS: ChartType[] = ["table"];

const BREAKDOWNS: { value: ReplyBreakdown; label: string }[] = [
  { value: "none", label: "No breakdown" },
  { value: "pipeline", label: "By pipeline" },
  { value: "channel", label: "By channel" },
];

export function ResponsesPerTicket({
  layout,
  scope: pageScope,
  isManager,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
}) {
  const stored = layout.card("responses-per-ticket");
  /* The card's own Pipeline and Agent filters, folded into the page's
     scope so they reach both the query and its key. */
  const scope = withCardFilters(pageScope, stored);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);

  /* Stored on the card like the range and the chart, so a breakdown somebody
     works with every morning is still there tomorrow. */
  const breakdown = (BREAKDOWNS.find(
    (entry) => entry.value === stored.breakdown,
  )?.value ?? "none") as ReplyBreakdown;

  const report = useQuery({
    queryKey: queryKeys.dashboardReport("responses-per-ticket", range, scope, breakdown),
    queryFn: () => api.responsesPerTicket(range, scope, breakdown),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  /*
   * One agent, because the card only renders on an agent view. Read as "the
   * first row" rather than by matching `scope.userId`, so a ticket closed while
   * unassigned — which comes back with a null agent id — is still shown rather
   * than silently becoming an empty card.
   */
  const agent = report.data?.agents[0] ?? null;

  return (
    <ReportCard
      id="responses-per-ticket"
      title="Responses per ticket"
      subtitle="Replies the desk sent per closed ticket — customer replies and internal notes are not counted"
      layout={layout}
      offerAgents={isManager}
      charts={CHARTS}
      hideChart
      actions={
        <Select
          value={breakdown}
          onChange={(next) => layout.setCard("responses-per-ticket", { breakdown: next })}
          label="Break the figure down by"
          options={BREAKDOWNS.map((entry) => ({
            value: entry.value,
            label: entry.label,
          }))}
        />
      }
      footnote={
        report.data?.truncated ? (
          <p>
            More tickets closed in this period than one read covers, so these
            figures are over the earliest of them. Narrow the range.
          </p>
        ) : null
      }
    >
      {() => {
        if (!ready) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              Pick both dates.
            </p>
          );
        }

        if (!agent) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              {report.isLoading
                ? "Loading…"
                : "Nothing closed in this period, so there is nothing to count."}
            </p>
          );
        }

        return <AgentFigures agent={agent} />;
      }}
    </ReportCard>
  );
}

function AgentFigures({ agent }: { agent: AgentReplies }) {
  return (
    <div className="space-y-3">
      <Figure distribution={agent.overall} />

      {agent.breakdown.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-faint">
                <th className="py-1 font-medium">Group</th>
                <th className="py-1 text-right font-medium">Typical</th>
                <th className="py-1 text-right font-medium">p90</th>
                <th className="py-1 text-right font-medium">p95</th>
                <th className="py-1 text-right font-medium">Closed</th>
              </tr>
            </thead>
            <tbody>
              {agent.breakdown.map((row) => (
                <tr key={row.key} className="border-t border-line">
                  <td className="py-1.5 text-content">{row.label}</td>
                  <td className="py-1.5 text-right tabular-nums text-content">
                    {count(row.distribution.median)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted">
                    {count(row.distribution.p90)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted">
                    {count(row.distribution.p95)}
                  </td>
                  {/* The population, on every row. A median over three tickets
                      and one over three hundred must not read alike. */}
                  <td className="py-1.5 text-right tabular-nums text-faint">
                    {row.distribution.tickets}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The headline: one big number, the tail small beside it.
 *
 * Laid out like the figures on the velocity card rather than as a tile of its
 * own, so a standalone number reads the same wherever it appears on this page.
 */
function Figure({ distribution }: { distribution: ReplyDistribution }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-faint">
        Typical ticket
      </p>
      <p className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-content">
          {count(distribution.median)}
        </span>
        <span
          className="text-xs text-muted"
          title="The 90th and 95th percentiles: how many replies the hard tickets take. Shown beside the median because the two together are the reading — a typical ticket that is fine and a tail that runs away is a different problem from both being high."
        >
          p90 {count(distribution.p90)} · p95 {count(distribution.p95)}
        </span>
      </p>
      <p className="text-[11px] text-faint">
        median over {distribution.tickets} closed
      </p>
    </div>
  );
}

/** A reply count, or a dash where there was nothing to count. */
function count(value: number | null): string {
  if (value === null) return "—";
  /* One decimal only when interpolation produced one. A median of 2 should
     read "2", not "2.0" -- these are counts of things, not measurements. */
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
